export type InstitutionType = 'cc' | 'public' | 'private' | 'careeru' | 'careerc' | 'training'
export type TermYears = 1 | 2 | 3

import { calculateSharedValue, SHARED_VALUE_MODEL_VERSION, type SharedValueInputs, type SharedValueResult } from './shared-value'

export const INSTITUTION_DEFAULTS: Record<InstitutionType, {
  label: string
  enrolled: number
  revenuePerStudent: number
  attritionPercent: number
  advisors: number
}> = {
  cc: { label: 'Community College', enrolled: 12_000, revenuePerStudent: 9_000, attritionPercent: 28, advisors: 8 },
  public: { label: 'Public University', enrolled: 25_000, revenuePerStudent: 17_000, attritionPercent: 12, advisors: 16 },
  private: { label: 'Private University', enrolled: 6_000, revenuePerStudent: 32_000, attritionPercent: 12, advisors: 4 },
  careeru: { label: 'Career-focused university', enrolled: 12_000, revenuePerStudent: 16_000, attritionPercent: 20, advisors: 8 },
  careerc: { label: 'Career college or trade school', enrolled: 3_000, revenuePerStudent: 14_000, attritionPercent: 25, advisors: 2 },
  training: { label: 'Career training program', enrolled: 1_000, revenuePerStudent: 10_000, attritionPercent: 18, advisors: 1 },
}

export interface EnrollmentRoiInput {
  institutionType: InstitutionType
  enrolled: number
  revenuePerStudent: number
  attritionPercent: number
  retentionLiftPercent: number
  advisors: number
  studentsServed: number
  implementationScope: 1 | 1.5 | 2
  termYears: TermYears
  /** Optional shared value context for customer-provided, framework-neutral metrics. */
  sharedValue?: SharedValueInputs
}

export interface PaymentLine {
  label: string
  timing: string
  amount: number
  originalAmount?: number
  retained?: boolean
  included?: boolean
}

export interface EnrollmentRoiResult {
  valueModelVersion: typeof SHARED_VALUE_MODEL_VERSION
  sharedValue: SharedValueResult
  revenueAtRisk: number
  studentsLost: number
  studentsRetained: number
  tuitionKept: number
  recruitmentAvoided: number
  newStudents: number
  enrollmentValue: number
  advisorsNeeded: number
  advisorsAvoided: number
  staffingValue: number
  totalYearOneValue: number
  /** Framework-neutral annual opportunity value; quote price never changes it. */
  annualValue: number
  annualPlatform: number
  platformTerm: number
  implementationBase: number
  implementationDue: number
  optOutImplementationRetained: number
  optOutSubscriptionRefund: number
  totalContract: number
  roiMultiple: number
  customPricing: boolean
  pricingBand: string
  paymentSchedule: PaymentLine[]
}

const RECRUITMENT_COST = 494
const ADVISOR_COST = 70_000
const SUPPORT_RATIO = 600
const ENROLLMENT_LIFT = 0.005
const IMPLEMENTATION_RATE = 0.2
const IMPLEMENTATION_FLOOR = 5_000

const PLATFORM_BANDS = [
  { max: 1_000, fee: 15_000, label: 'Under 1,000' },
  { max: 2_500, fee: 30_000, label: '1,000 to 2,500' },
  { max: 5_000, fee: 50_000, label: '2,500 to 5,000' },
  { max: 10_000, fee: 100_000, label: '5,000 to 10,000' },
  { max: 15_000, fee: 150_000, label: '10,000 to 15,000' },
  { max: 20_000, fee: 200_000, label: '15,000 to 20,000' },
  { max: 30_000, fee: 300_000, label: '20,000 to 30,000' },
  { max: 40_000, fee: 400_000, label: '30,000 to 40,000' },
  { max: 50_000, fee: 500_000, label: '40,000 to 50,000' },
  { max: Number.POSITIVE_INFINITY, fee: 500_000, label: '50,000+', custom: true },
] as const

export const DEFAULT_ENROLLMENT_INPUT: EnrollmentRoiInput = {
  institutionType: 'public',
  enrolled: 25_000,
  revenuePerStudent: 17_000,
  attritionPercent: 12,
  retentionLiftPercent: 2,
  advisors: 16,
  studentsServed: 6_000,
  implementationScope: 1,
  termYears: 1,
}

export function calculateEnrollmentRoi(input: EnrollmentRoiInput): EnrollmentRoiResult {
  const enrolled = Math.max(0, input.enrolled)
  const revenuePerStudent = Math.max(0, input.revenuePerStudent)
  const attrition = Math.max(0, input.attritionPercent) / 100
  const retentionLift = Math.max(0, input.retentionLiftPercent) / 100
  const studentsLost = enrolled * attrition
  const revenueAtRisk = studentsLost * revenuePerStudent
  const studentsRetained = enrolled * retentionLift
  const tuitionKept = studentsRetained * revenuePerStudent
  const recruitmentAvoided = studentsRetained * RECRUITMENT_COST
  const newStudents = enrolled * ENROLLMENT_LIFT
  const enrollmentValue = newStudents * revenuePerStudent
  const advisorsNeeded = enrolled / SUPPORT_RATIO
  const advisorsAvoided = Math.max(0, advisorsNeeded - Math.max(0, input.advisors))
  const staffingValue = advisorsAvoided * ADVISOR_COST
  const sharedValue = calculateSharedValue({
    learnerCount: enrolled,
    revenuePerLearner: revenuePerStudent,
    retentionLiftPercent: input.retentionLiftPercent,
    improvementLiftPercent: ENROLLMENT_LIFT * 100,
    retainedLearners: studentsRetained,
    improvementLearners: newStudents,
    avoidedRecruitmentCount: studentsRetained,
    avoidedRecruitmentUnitCost: RECRUITMENT_COST,
    teamCapacityValue: staffingValue,
    ...input.sharedValue,
  })
  const sharedAmount = (id: 'retained_revenue' | 'enrollment_or_completion' | 'recruitment_or_replacement' | 'team_capacity'): number => sharedValue.components.find((component) => component.id === id)?.amount || 0
  const reportedStudentsRetained = input.sharedValue?.retainedLearners ?? studentsRetained
  const reportedNewStudents = input.sharedValue?.improvementLearners ?? newStudents
  const reportedTuitionKept = sharedAmount('retained_revenue')
  const reportedRecruitmentAvoided = sharedAmount('recruitment_or_replacement')
  const reportedEnrollmentValue = sharedAmount('enrollment_or_completion')
  const reportedStaffingValue = sharedAmount('team_capacity')
  const totalYearOneValue = sharedValue.annualValue

  const band = PLATFORM_BANDS.find((candidate) => enrolled <= candidate.max) ?? PLATFORM_BANDS.at(-1)!
  const annualPlatform = band.fee
  const platformTerm = annualPlatform * input.termYears
  const implementationBase = Math.max(IMPLEMENTATION_FLOOR, annualPlatform * IMPLEMENTATION_RATE * input.implementationScope)
  const implementationDue = input.termYears === 2 ? implementationBase * 0.5 : input.termYears === 3 ? 0 : implementationBase
  const optOutImplementationRetained = implementationDue
  const optOutSubscriptionRefund = platformTerm
  const totalContract = platformTerm + implementationDue
  const paymentSchedule: PaymentLine[] = [
    { label: 'At signature', timing: `Full ${input.termYears}-year contract`, amount: totalContract },
    {
      label: 'Implementation fee',
      timing: input.termYears === 3 ? 'Waived with a 3-year agreement' : input.termYears === 2 ? '50% off with a 2-year agreement' : 'One-time setup and launch',
      amount: implementationDue,
      ...(input.termYears > 1 ? { originalAmount: implementationBase } : {}),
      included: true,
    },
  ]
  paymentSchedule.push({
    label: '90-Day Opt Out',
    timing: 'Prepaid partnership fee refunded',
    amount: optOutImplementationRetained,
    retained: true,
  })

  return {
    valueModelVersion: SHARED_VALUE_MODEL_VERSION,
    sharedValue,
    revenueAtRisk,
    studentsLost,
    studentsRetained: reportedStudentsRetained,
    tuitionKept: reportedTuitionKept,
    recruitmentAvoided: reportedRecruitmentAvoided,
    newStudents: reportedNewStudents,
    advisorsNeeded,
    advisorsAvoided,
    enrollmentValue: reportedEnrollmentValue,
    staffingValue: reportedStaffingValue,
    totalYearOneValue,
    annualValue: totalYearOneValue,
    annualPlatform,
    platformTerm,
    implementationBase,
    implementationDue,
    optOutImplementationRetained,
    optOutSubscriptionRefund,
    totalContract,
    roiMultiple: totalContract > 0 ? totalYearOneValue / totalContract : 0,
    customPricing: Boolean('custom' in band && band.custom),
    pricingBand: band.label,
    paymentSchedule,
  }
}
